import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { Toaster } from "@/components/ui/sonner";
import { LaunchLockGate } from "@/components/launch-lock-gate";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { initSentryClient } from "../lib/monitoring/sentry-client";

if (typeof window !== "undefined") {
  initSentryClient();
}

function NotFoundComponent() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-2xl border bg-card p-10 text-center shadow-card">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary text-navy">
          <span className="text-2xl font-semibold">?</span>
        </div>
        <h1 className="mt-6 text-6xl text-navy">404</h1>
        <h2 className="mt-2 text-lg font-semibold tracking-tight">Page not found</h2>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-8">
          <Link
            to="/"
            className="inline-flex h-10 items-center justify-center rounded-lg bg-navy px-5 text-sm font-medium text-navy-foreground shadow-sm transition hover:opacity-90 hover:shadow-md"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-2xl border bg-card p-10 text-center shadow-card">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <span className="text-2xl">!</span>
        </div>
        <h1 className="mt-6 text-xl font-semibold tracking-tight">This page didn't load</h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Something went wrong. Try again or head back home.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex h-10 items-center justify-center rounded-lg bg-navy px-5 text-sm font-medium text-navy-foreground shadow-sm transition hover:opacity-90 hover:shadow-md"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex h-10 items-center justify-center rounded-lg border border-input bg-background px-5 text-sm font-medium transition hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "ClaimGuard — AI Invoice Reviews & Full-Service Negotiation" },
      {
        name: "description",
        content:
          "Submit any storage or towing invoice. Our AI reviews it against state laws and local rates, then delivers a professional findings and negotiation package.",
      },
      { property: "og:title", content: "ClaimGuard — AI Invoice Reviews & Full-Service Negotiation" },
      {
        property: "og:description",
        content:
          "Snap a photo, upload a file, or forward an email. Get a professional findings and negotiation package in minutes.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "ClaimGuard — AI Invoice Reviews & Full-Service Negotiation" },
      { name: "twitter:description", content: "Snap a photo, upload a file, or forward an email. Get a professional findings and negotiation package in minutes." },
      { property: "og:site_name", content: "ClaimGuard" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.ico", type: "image/x-icon" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Inter:wght@400;500;600;700&display=swap",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head suppressHydrationWarning>
        <HeadContent />
      </head>
      <body suppressHydrationWarning>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <LaunchLockGate>
        <Outlet />
      </LaunchLockGate>
      <Toaster position="top-right" richColors closeButton />
    </QueryClientProvider>
  );
}
